// =============================================================================
// Normascan — Plateforme d'analyse normative par IA
// Copyright (c) 2026-2027 Evalens SAS — Tous droits réservés
// Marque Normascan déposée INPI n°5242986 (classes 9 et 42)
// Site : https://app.normascan.fr
// =============================================================================
import React, { useState, useEffect } from 'react';
import { useRoute, useLocation } from 'wouter';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../i18n';
import { setupMfa, confirmMfaSetup } from '../services/api';
import MfaSetupFlow from '../components/MfaSetupFlow';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://app.normascan.fr';

const InviteAccept = () => {
  const { t } = useI18n();
  const [, params] = useRoute('/invite/:token');
  const [, setLocation] = useLocation();
  const { login } = useAuth();

  const token = params?.token;

  const [inviteInfo, setInviteInfo] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [loading, setLoading] = useState(true);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  // L'acceptation doit être un geste de l'invité : le serveur refuse la création
  // sans elle, cette case n'est que la façon de l'exprimer.
  const [cguAccepted, setCguAccepted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // MFA obligatoire par la licence : configuration forcée avant toute session complète
  const [mfaStep, setMfaStep] = useState(null); // null | 'setup_required'
  const [mfaTempToken, setMfaTempToken] = useState(null);

  useEffect(() => {
    if (!token) {
      setLoadError(t('invite_invalid_link'));
      setLoading(false);
      return;
    }
    fetch(`${API_BASE_URL}/api/invite/${token}`)
      .then(r => r.json().then(d => ({ ok: r.ok, data: d })))
      .then(({ ok, data }) => {
        if (!ok) {
          setLoadError(data.error || t('invite_invalid_link'));
        } else {
          setInviteInfo(data);
        }
      })
      .catch(() => setLoadError(t('invite_invalid_link')))
      .finally(() => setLoading(false));
  }, [token, t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');

    if (password !== confirmPassword) {
      setSubmitError(t('invite_passwords_mismatch'));
      return;
    }
    if (password.length < 8) {
      setSubmitError(t('invite_password_too_short'));
      return;
    }
    if (!cguAccepted) {
      setSubmitError(t('invite_cgu_required'));
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/invite/${token}/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password, cgu_accepted: true }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || t('invite_error_generic'));
      }
      if (data.status === 'mfa_setup_required') {
        setMfaTempToken(data.temp_token);
        setMfaStep('setup_required');
        setSubmitting(false);
        return;
      }
      login(data.user);
      setLocation('/dashboard');
    } catch (err) {
      setSubmitError(err.message || t('invite_error_generic'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleMfaComplete = (result) => {
    login(result.user);
    setLocation('/dashboard');
  };

  if (mfaStep === 'setup_required') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md space-y-4">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">{t('login_mfa_setup_required_title')}</h2>
            <p className="text-sm text-gray-700 mt-2">{t('login_mfa_setup_required_intro')}</p>
          </div>
          <MfaSetupFlow
            onSetup={() => setupMfa(mfaTempToken)}
            onConfirm={(code) => confirmMfaSetup(code, mfaTempToken)}
            onClose={() => setMfaStep(null)}
            onComplete={handleMfaComplete}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <a href="/">
            <span className="inline-flex items-center gap-2 mb-6 cursor-pointer">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center">
                <span className="text-white text-xl font-bold">N</span>
              </div>
              <span className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                Normascan
              </span>
            </span>
          </a>
        </div>

        {loading && (
          <Card>
            <CardContent className="pt-6 text-center text-gray-500 py-10">
              {t('invite_loading')}
            </CardContent>
          </Card>
        )}

        {!loading && loadError && (
          <Card>
            <CardHeader>
              <CardTitle className="text-red-600">{t('invite_invalid_title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 mb-4">{loadError}</p>
              <a href="/login" className="text-blue-600 hover:underline text-sm">
                {t('invite_back_to_login')}
              </a>
            </CardContent>
          </Card>
        )}

        {!loading && !loadError && inviteInfo && (
          <Card>
            <CardHeader>
              <CardTitle>{t('invite_title')}</CardTitle>
              <CardDescription>
                {t('invite_subtitle_prefix')} <strong>{inviteInfo.license_nom}</strong>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {submitError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">
                    {submitError}
                  </div>
                )}

                <div className="space-y-2">
                  <Label>{t('invite_email_label')}</Label>
                  <Input
                    type="email"
                    value={inviteInfo.email}
                    disabled
                    className="bg-gray-50"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">{t('invite_password_label')}</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    minLength={8}
                    disabled={submitting}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">{t('invite_confirm_password_label')}</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    required
                    disabled={submitting}
                  />
                </div>

                <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    id="cguAccepted"
                    type="checkbox"
                    checked={cguAccepted}
                    onChange={e => setCguAccepted(e.target.checked)}
                    className="mt-1"
                    disabled={submitting}
                  />
                  <span>
                    {t('invite_cgu_prefix')}{' '}
                    <a href="/cgv" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                      {t('invite_cgu_terms')}
                    </a>{' '}
                    {t('invite_cgu_and')}{' '}
                    <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                      {t('invite_cgu_privacy')}
                    </a>
                  </span>
                </label>

                <Button type="submit" className="w-full" disabled={submitting || !cguAccepted}>
                  {submitting ? t('invite_submitting') : t('invite_submit')}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default InviteAccept;
